import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ProvenanceFilters = ({ 
  onFiltersChange, 
  onSearch,
  className = '' 
}) => {
  const [filters, setFilters] = useState({
    dateRange: 'all',
    transactionType: 'all',
    registry: 'all',
    status: 'all'
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);

  const dateRangeOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const transactionTypeOptions = [
    { value: 'all', label: 'All Transaction Types' },
    { value: 'issuance', label: 'Issuance' },
    { value: 'transfer', label: 'Transfer' },
    { value: 'tokenization', label: 'Tokenization' },
    { value: 'split', label: 'Split' },
    { value: 'retirement', label: 'Retirement' }
  ];

  const registryOptions = [
    { value: 'all', label: 'All Registries' },
    { value: 'verra', label: 'Verra VCS' },
    { value: 'gold-standard', label: 'Gold Standard' },
    { value: 'acr', label: 'American Carbon Registry' },
    { value: 'art', label: 'Architecture for REDD+ Transactions' },
    { value: 'gcc', label: 'Global Carbon Council' },
    { value: 'cad-trust', label: 'CAD Trust' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    { value: 'verified', label: 'Verified' },
    { value: 'completed', label: 'Completed' },
    { value: 'active', label: 'Active' },
    { value: 'retired', label: 'Retired' },
    { value: 'pending', label: 'Pending' }
  ];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    onSearch(query);
  };

  const clearAllFilters = () => {
    const defaultFilters = {
      dateRange: 'all',
      transactionType: 'all',
      registry: 'all',
      status: 'all'
    };
    setFilters(defaultFilters);
    setSearchQuery('');
    onFiltersChange(defaultFilters);
    onSearch('');
  };

  const getActiveFilterCount = () => {
    return Object.values(filters)?.filter(value => value !== 'all')?.length;
  };

  return (
    <div className={`bg-card border border-border rounded-lg ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="Filter" size={20} />
          <h3 className="text-lg font-semibold text-foreground">Provenance Filters</h3>
          {getActiveFilterCount() > 0 && (
            <span className="px-2 py-1 bg-primary text-primary-foreground text-xs font-medium rounded-full">
              {getActiveFilterCount()} active
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={clearAllFilters}
            className="text-sm text-muted-foreground hover:text-foreground transition-smooth"
          >
            Clear All
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
          </button>
        </div>
      </div>
      {/* Search Bar */}
      <div className="p-4 border-b border-border">
        <Input
          type="search"
          placeholder="Search by serial number, transaction ID, or company name..."
          value={searchQuery}
          onChange={(e) => handleSearch(e?.target?.value)}
          className="w-full"
        />
      </div>
      {/* Filter Controls */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Date Range Filter */}
            <Select
              label="Date Range"
              options={dateRangeOptions}
              value={filters?.dateRange}
              onChange={(value) => handleFilterChange('dateRange', value)}
            />

            {/* Transaction Type Filter */}
            <Select
              label="Transaction Type"
              options={transactionTypeOptions}
              value={filters?.transactionType}
              onChange={(value) => handleFilterChange('transactionType', value)}
            />

            {/* Registry Filter */}
            <Select
              label="Registry"
              options={registryOptions}
              value={filters?.registry}
              onChange={(value) => handleFilterChange('registry', value)}
            />

            {/* Status Filter */}
            <Select
              label="Status"
              options={statusOptions}
              value={filters?.status}
              onChange={(value) => handleFilterChange('status', value)}
            />
          </div>

          {/* Custom Date Range */}
          {filters?.dateRange === 'custom' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
              <Input
                type="date"
                label="Start Date"
                placeholder="Select start date"
              />
              <Input
                type="date"
                label="End Date"
                placeholder="Select end date"
              />
            </div>
          )}

          {/* Quick Actions */}
          <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
            <button
              onClick={() => handleFilterChange('transactionType', 'retirement')}
              className="px-3 py-1 bg-error/10 text-error text-xs font-medium rounded-full hover:bg-error/20 transition-smooth"
            >
              Show Retirements Only
            </button>
            <button
              onClick={() => handleFilterChange('transactionType', 'tokenization')}
              className="px-3 py-1 bg-warning/10 text-warning text-xs font-medium rounded-full hover:bg-warning/20 transition-smooth"
            >
              Show Tokenized Credits
            </button>
            <button
              onClick={() => handleFilterChange('status', 'verified')}
              className="px-3 py-1 bg-success/10 text-success text-xs font-medium rounded-full hover:bg-success/20 transition-smooth"
            >
              Verified Only
            </button>
          </div>
        </div>
      )}
      {/* Active Filters Summary */}
      {getActiveFilterCount() > 0 && (
        <div className="p-4 bg-muted/50 border-t border-border">
          <div className="flex items-center space-x-2 text-sm text-muted-foreground">
            <Icon name="Info" size={16} />
            <span>
              Showing results filtered by: {
                Object.entries(filters)?.filter(([_, value]) => value !== 'all')?.map(([key, value]) => `${key}: ${value}`)?.join(', ')
              }
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProvenanceFilters;